import {
    BookingflowStateMachine,
    CheckoutActions,
    CheckoutState,
    GeneralActions,
} from '@raskrask-dk/bookingflow-state-machine'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { setPaymentInfo } from '../../redux/reducers/checkoutSettings'
import {
    GetData,
} from '@raskrask-dk/bookingflow-state-machine'
import { setErrorcase } from '../../redux/reducers/appSettings'
import { IGetPaymentInfo, IProductDetails, Booking, IProductDetailsBooking, IProductDetailsKlippeCard, IReepayCustomerRaw, IDiscountOptions, IBookingDiscountOptions, Company, Customer, Package, ProductType } from '@raskrask-dk/raskrask-utils'
import { GiftCard, GoDreamGiftCard, KlippeCard, ThirdPartyGiftCard } from '@raskrask-dk/raskrask-utils/lib/types/bfsm/classes/giftcard'

export const useCheckout = (
    BFSM: BookingflowStateMachine
): { shouldShowPaymentMethods: boolean } => {
    const dispatch = useDispatch()

    const [shouldShowPaymentMethods, setShouldShowPaymentMethods] =
        useState(false)

    useEffect(() => {
        const data = BFSM.getData()
        const getPaymentInfo = async () => {
            try {
                const paymentInfo = adaptorToGetPaymentInfoOnCheckoutState(data)
                const result = await BFSM.repo.getPaymentInfo(paymentInfo)
                BFSM.doAction(CheckoutActions.SetPrice, result.price)
                setShouldShowPaymentMethods(result.price > 0 ? true : false) // Alternatively setShouldPay(useBFSMCanDo(CheckoutActions.Next))
                dispatch(setPaymentInfo(result))
            } catch (err) {
                dispatch(setErrorcase('GetPaymentInfoError'))
                BFSM.doAction(GeneralActions.GoToErrorState, undefined)
            }
        }

        getPaymentInfo()
    }, [])

    return { shouldShowPaymentMethods }
}

export const adaptorToGetPaymentInfoOnCheckoutState = (
    data: GetData<CheckoutState>
): IGetPaymentInfo => {
    const productType = data.product.type as ProductType
    const productDetails = constructProductDetails(data)
    const discountOptions = constructDiscountDetails(data)
    const customer = constructCustomer(data)
    return {
        productType,
        productDetails,
        ...(customer && { customer }),
        ...(discountOptions && { discountOptions }),
    }
}

const constructProductDetails = (
    data: GetData<CheckoutState>
): IProductDetails => {
    const productType = data.product.type
    if (productType === 'Booking') {
        const booking = data.product as Booking
        /**This might not work. Alexander removed the partner from the package.
         * this has to be reworked.
         */
        return {
            serviceType: {
                serviceTypeName: booking.service.name,
                serviceType_ID: booking.service.id,
                duration: booking.service.duration.toMillis() / 60 / 1000,
            },
            serviceDate: `${booking.serviceDate}:00`,
            partner: {
                profileId: booking.partner?.id,
                alias: booking.partner?.alias,
            },
        } as IProductDetailsBooking
    }
    if (productType == 'Package') {
        const klippecard = data.product as Package
        return {
            serviceType: {
                serviceTypeName: klippecard.service.name,
                serviceType_ID: klippecard.service.id,
                duration: klippecard.service.duration.toMillis() / 60 / 1000,
            },
            partner: {
                profileId: klippecard.partner?.id,
                alias: klippecard.partner?.alias,
            },
            packageId: klippecard.id,
        } as IProductDetailsKlippeCard
    }
    throw new Error('Something went wrong')
}

const constructCustomer = (
    data: GetData<CheckoutState>
): IReepayCustomerRaw | undefined => {
    switch (data.customer.type) {
        case 'Company': {
            const { email, phone, adminName } = data.customer as Company
            if (email && adminName && phone) {
                return {
                    email: email,
                    firstName: adminName.split(' ').slice(0, -1).join(' '),
                    lastName: adminName.split(' ').slice(-1).join(' '),
                    phone: phone.toString(),
                    customerType: 'Company',
                }
            }
            break
        }
        default: {
            const customer = data.customer as Customer
            if (customer.email && customer.name && customer.phone)
                return {
                    email: customer.email,
                    firstName: customer.name.split(' ').slice(0, -1).join(' '),
                    lastName: customer.name.split(' ').slice(-1).join(' '),
                    phone: customer.phone.toString(),
                    customerType: 'Private',
                }
            break
        }
    }
    return undefined
}

const constructDiscountDetails = (
    data: GetData<CheckoutState>
): IDiscountOptions | undefined => {
    const promoCode = data.promoCode
    const productType = data.product.type
    if (productType == 'Booking') {
        switch (data.voucher?.type) {
            case 'GiftCard': {
                const { code } = data.voucher as GiftCard
                return {
                    code: code,
                    promoCode: promoCode,
                } as IBookingDiscountOptions
            }
            case 'ThirdPartyGiftCard': {
                const { code, providerId } = data.voucher as ThirdPartyGiftCard
                return {
                    code: code,
                    partnerGiftCardId: providerId,
                    promoCode: promoCode,
                } as IBookingDiscountOptions
            }
            case 'GoDreamGiftCard': {
                const { code, providerId, activationCode } =
                    data.voucher as GoDreamGiftCard
                return {
                    code: `${code} ${activationCode}`,
                    partnerGiftCardId: providerId,
                    promoCode: promoCode,
                } as IBookingDiscountOptions
            }
            case 'KlippeCard': {
                const { code } = data.voucher as KlippeCard
                return {
                    code: code,
                } as IBookingDiscountOptions
            }
            default: {
                break
            }
        }
    }
    if (productType == 'Package') {
        return undefined
    }
    return undefined
}
